/**
 * `SCR-COMMISSION` (10 states), `SCR-CLOTHSTEP` (4), `SCR-CONFIG` (26).
 * FLOWS_SPEC §6. OWNER: web-frontend-engineer.
 *
 * COMMISSION-FIRST (M-1). Four named steps in the client's language, never
 * numbered "3 of 6".
 *
 * `SCR-CONFIG` is the RULING-005 screen: the hero swaps between the mirror and
 * the written specification, and `OptionColumn` is THE SAME COMPONENT in both.
 * An entire order can be placed without ever rendering `src/three`.
 */

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import type { OptionState } from '@pt/core';
import { api } from '../lib/api.ts';
import { useDraft } from '../lib/draft.ts';
import { useResource } from '../lib/resource.ts';
import { commissionTypeLabel } from '../lib/optionCopy.ts';
import { decimal, money } from '@pt/view';
import { AppShell } from '../app/AppShell.tsx';
import { useNavigation } from '../app/Router.tsx';
import { useAnnouncer } from '../app/Announcer.tsx';
import { useHouse } from '../app/SessionProvider.tsx';
import { OptionColumn, OptionGroup, PriceDisplay, setCanvasAmounts } from '../components/commission.tsx';
import { Button, FactLine, RovingRadioGroup, Statement } from '../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../components/states.tsx';
import { BookScreen } from './Book.tsx';
import { availabilityText } from '../components/cloth.tsx';

import type { GarmentCameraState, GarmentFabric, GarmentReadyInfo } from '../three/types.ts';
import { optionalLabel } from '../lib/label.ts';
import { refusalCopy } from '../lib/refusal.ts';
import { joinList } from '@pt/view';

/** Lazily loaded so the ORDER PATH NEVER DEPENDS ON `src/three` (invariant 17). */
const GarmentPreview = lazy(async () => {
  const module = await import('../three/GarmentPreview.tsx');
  return { default: module.GarmentPreview };
});

/**
 * The held cloth's NAME. `API-CONFIG-EVAL`'s `clothContext` carries the id, the
 * metreage and the selectability but not the name, and `ST-COMM-HELDCLOTH` and
 * `ST-CLOTHSTEP-CHOSEN` both have to name it — "Held: {clothName}". One extra
 * read rather than a name invented from an id. Reported to the backend engineer
 * as a one-field addition to `clothContext`.
 */
function useHeldClothName(clothId: string | null): string {
  const cloth = useResource(
    () => (clothId === null ? Promise.resolve(null) : api.fabric(clothId)),
    [clothId],
  );
  return cloth.data?.name ?? '';
}

/* --------------------------------------------------------- SCR-COMMISSION */

export function CommissionScreen({ seedClothId }: { seedClothId?: string }) {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const draft = useDraft({ create: true, ...(seedClothId === undefined ? {} : { seedClothId }) });
  const { say } = useAnnouncer();
  const [pressedForward, setPressedForward] = useState(false);

  const evaluation = draft.evaluation;
  const heldClothName = useHeldClothName(evaluation?.clothContext?.clothId ?? null);
  const option = (id: string): OptionState | undefined =>
    evaluation?.options.find((o) => o.optionId === id);

  const type = option('commission.type');
  const occasion = option('commission.occasion');
  const fit = option('commission.fit_profile');
  const season = option('commission.target_season');
  const [changingFit, setChangingFit] = useState(false);
  const [changingSeason, setChangingSeason] = useState(false);

  const unanswered =
    type?.currentValue == null ? type : occasion?.currentValue == null ? occasion : null;

  /* 5 September 2026 (finding C10): "Saved." was announced the moment the draft
     LOADED — the first evaluation is a read, and nothing had been saved. The
     polite region kept that word as its standing text, so a text dump of the
     page ended in a lone "Saved." before the client had touched anything. An
     announcement states a thing that happened: it is made only for an
     evaluation that followed a change of his, which `useDraft` writes to the
     atelier before it comes back here. */
  const evaluatedOnce = useRef(false);
  useEffect(() => {
    if (evaluation === null) return;
    if (!evaluatedOnce.current) {
      evaluatedOnce.current = true;
      return;
    }
    say(t('comm.saved.announce'));
  }, [evaluation, say]);

  return (
    <AppShell
      title={t('comm.heading')}
      ground="book"
      banner={draft.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {draft.phase === 'loading' ? (
        <LoadingRegion rows={4} variant="block" slow={false} label={t('comm.heading')} />
      ) : null}
      {draft.phase === 'error' ? (
        <FetchError whatKey="comm.error.what" onRetry={draft.reload} />
      ) : null}
      {draft.rejection === null ? null : (
        <FetchError whatKey="comm.error.means" onRetry={draft.reload} />
      )}

      {/* ST-COMM-NORECORD — the flow cannot start; a master tailor authors the
          record. No configurator, no placeholders. */}
      {evaluation === null ? null : evaluation.options.length === 0 ? (
        <section>
          <Statement>{t('comm.norecord.statement')}</Statement>
          <div className="pt-actions">
            <Button onClick={() => navigate('/measurements/remeasure')}>
              {t('comm.norecord.action')}
            </Button>
          </div>
        </section>
      ) : (
        <>
          {/* ST-COMM-HELDCLOTH — the cloth is named and held, visibly, ABOVE the
              questions. The questions still come first; the cloth waits. */}
          {evaluation.clothContext === null ? null : (
            <Statement>
              {t('comm.heldcloth.statement', { clothName: heldClothName })}
            </Statement>
          )}

          {type === undefined ? null : (
            <OptionGroup option={type} onChange={draft.setOption} busy={draft.busyOptionId === type.optionId} />
          )}
          {occasion === undefined ? null : (
            <OptionGroup
              option={occasion}
              onChange={draft.setOption}
              busy={draft.busyOptionId === occasion.optionId}
            />
          )}

          {/* Two STATED FACTS, not questions, each changeable in one action. */}
          {fit === undefined ? null : changingFit ? (
            <OptionGroup option={fit} onChange={(id, value) => { draft.setOption(id, value); setChangingFit(false); }} />
          ) : (
            <div className="pt-block">
              <FactLine>
                {t('comm.fit.stated', {
                  fitProfileLabel:
                    fit.currentValue == null
                      ? ''
                      : tDynamic(`config.option.commission.fit_profile.value.${String(fit.currentValue)}`),
                })}
              </FactLine>
              <Button onClick={() => setChangingFit(true)}>{t('comm.fit.change')}</Button>
            </div>
          )}

          {season === undefined ? null : changingSeason ? (
            <OptionGroup option={season} onChange={(id, value) => { draft.setOption(id, value); setChangingSeason(false); }} />
          ) : (
            <div className="pt-block">
              <FactLine>
                {t('comm.season.stated', {
                  seasonLabel:
                    season.currentValue == null
                      ? ''
                      : tDynamic(
                          `config.option.commission.target_season.value.${String(season.currentValue)}`,
                        ),
                })}
              </FactLine>
              <Button onClick={() => setChangingSeason(true)}>{t('comm.season.change')}</Button>
            </div>
          )}

          {/* EL-PRICE in ST-PRICE-UNAVAILABLE until a cloth is chosen.
              Never £0, never a "from" price. */}
          <PriceDisplay
            state={evaluation.quote === null ? 'unavailable' : 'current'}
            quote={evaluation.quote}
            tailorName={tailor?.displayName ?? ''}
          />

          {/* ST-COMM-FORWARD — the forward action is NEVER disabled. Pressing it
              with a question unanswered moves focus to it and names it. */}
          {pressedForward && unanswered !== null && unanswered !== undefined ? (
            <Statement>
              {t('comm.forward.unanswered', {
                optionLabel: tDynamic(`config.option.${unanswered.optionId}.label`),
              })}
            </Statement>
          ) : null}
          <div className="pt-actions">
            <Button
              variant="primary"
              onClick={() => {
                setPressedForward(true);
                if (unanswered !== null && unanswered !== undefined) {
                  document.getElementById(`option-${unanswered.optionId}`)?.scrollIntoView();
                  const focusable = document
                    .getElementById(`option-${unanswered.optionId}`)
                    ?.querySelector('button');
                  if (focusable instanceof HTMLElement) focusable.focus();
                  return;
                }
                navigate('/commission/cloth');
              }}
            >
              {t('comm.default.forward')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}

/* ---------------------------------------------------------- SCR-CLOTHSTEP */

/** `SCR-BOOK` in `ST-BOOK-INCONTEXT`, inside the flow, with the SAME components. */
export function ClothStepScreen() {
  const { navigate } = useNavigation();
  const draft = useDraft();
  const evaluation = draft.evaluation;
  const held = evaluation?.clothContext ?? null;
  const heldClothName = useHeldClothName(held?.clothId ?? null);
  const released = evaluation?.clothReleased ?? null;

  return (
    <AppShell title={t('clothstep.heading')} ground="book">
      {/* ST-CLOTHSTEP-INVALIDATED — released WITH THE REASON STATED, everything
          else preserved. Never a silent swap. */}
      {released === null ? null : (
        <Statement>
          {released.cause === 'discontinued'
            ? t('clothstep.invalidated.discontinued', { clothName: released.clothName })
            : released.cause === 'commissionChanged'
              ? t('clothstep.invalidated.commissionChanged', {
                  clothName: released.clothName,
                  commissionTypeLabel: commissionTypeLabel(
                    String(evaluation?.configuration['commission.type'] ?? ''),
                  ),
                  requirementMetres: decimal(released.requirementMetres ?? 0, 1),
                  availableMetres: decimal(released.availableMetres ?? 0, 1),
                })
              : t('clothstep.invalidated.stock', { clothName: released.clothName })}
        </Statement>
      )}

      {held === null ? (
        <Statement>{t('clothstep.none.forward')}</Statement>
      ) : (
        <section>
          <FactLine emphasis>
            {t('clothstep.chosen.held', {
              clothName: heldClothName,
              availabilityText: availabilityText({
                availableMetres: held.availableMetres,
                requirementMetres: held.requirementM,
                commissionTypeLabel: commissionTypeLabel(
                  String(evaluation?.configuration['commission.type'] ?? ''),
                ),
              }),
            })}
          </FactLine>
          {/* Changing the cloth voids the colour acknowledgement, and the screen
              says so BEFORE he changes it (P-FAB-05). */}
          <Statement>{t('clothstep.chosen.colourVoidNote')}</Statement>
          <div className="pt-actions pt-actions--equal">
            <Button variant="primary" onClick={() => navigate('/commission/make')}>
              {t('config.heading')}
            </Button>
          </div>
        </section>
      )}

      <BookScreen inFlow draftId={draft.draftId ?? undefined} />
    </AppShell>
  );
}

/* -------------------------------------------------------------- SCR-CONFIG */

type Hero = 'mirror' | 'spec';

export function ConfigScreen() {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const { say } = useAnnouncer();
  const draft = useDraft();
  const [hero, setHero] = useState<Hero>('mirror');
  const [heroUnavailable, setHeroUnavailable] = useState<
    null | { reason: string; missingFields: readonly string[] }
  >(null);
  const [slowRender, setSlowRender] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [pressedForward, setPressedForward] = useState(false);
  const [measurements, setMeasurements] = useState<Record<string, number | string> | null>(null);

  const evaluation = draft.evaluation;

  // The mirror needs the bound record's values. The shell holds them; the
  // renderer never fetches a record and never puts a measurement in a URL.
  useEffect(() => {
    let live = true;
    void api
      .measurements()
      .then((payload) => {
        if (!live) return;
        const current =
          payload.records.find((r) => r.recordId === payload.currentRecordId) ?? payload.records[0];
        setMeasurements((current?.values ?? null) as Record<string, number | string> | null);
      })
      .catch(() => setMeasurements(null));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (evaluation?.quote == null) return;
    const amounts: Record<string, string> = {};
    for (const line of evaluation.quote.lines) {
      if (line.optionId === 'jacket.canvas' && line.valueId != null) {
        amounts[String(line.valueId)] = money(
          Math.abs(line.amountMinor),
          evaluation.quote.currency,
        );
      }
    }
    setCanvasAmounts(amounts);
  }, [evaluation]);

  useEffect(() => {
    if (evaluation?.staleRules != null) {
      say(t('config.stalerules.statement', { changedSummary: evaluation.staleRules.changedSummary }));
    }
  }, [evaluation, say]);

  const missingRequired = evaluation?.missingRequired ?? [];
  const clothChosen = evaluation?.clothContext != null;
  // The mirror is only the hero when it can actually be one.
  const effectiveHero: Hero = heroUnavailable !== null ? 'spec' : hero;

  return (
    <AppShell
      title={t('config.heading')}
      ground="book"
      banner={draft.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {draft.phase === 'loading' ? (
        <LoadingRegion rows={6} variant="block" slow={false} label={t('config.heading')} />
      ) : null}
      {draft.phase === 'error' ? <FetchError whatKey="comm.error.what" onRetry={draft.reload} /> : null}
      {draft.rejection === null ? null : (
        <Statement>{refusalCopy(draft.rejection)}</Statement>
      )}
      {evaluation?.staleRules == null ? null : (
        <Statement>
          {t('config.stalerules.statement', {
            changedSummary: evaluation.staleRules.changedSummary,
          })}
        </Statement>
      )}

      {evaluation === null ? null : (
        <div className="pt-config">
          <div className="pt-config__hero">
            {/* The toggle. They are PEERS: same options, same price, same order. */}
            <fieldset className="pt-filter">
              <legend className="pt-filter__legend">{t('config.hero.label')}</legend>
              <RovingRadioGroup
                ariaLabel={t('config.hero.label')}
                values={['mirror', 'spec']}
                chosen={effectiveHero}
                labelOf={(value) => (value === 'mirror' ? t('config.hero.mirror') : t('config.hero.spec'))}
                onChoose={(value) => setHero(value as Hero)}
              />
            </fieldset>

            {/* ST-CONFIG-3D-UNSUPPORTED / -FAILED / -INCOMPLETE — stated as a
                FACT, not an apology, and the written specification becomes the
                hero. The option column and the price are untouched. */}
            {heroUnavailable === null ? null : (
              <Statement>
                {heroUnavailable.reason === 'webgl-unsupported'
                  ? t('config.3d.unsupported')
                  : heroUnavailable.reason === 'measurements-incomplete'
                    ? t('config.3d.incomplete', {
                        missingFieldsList: heroUnavailable.missingFields.join(', '),
                      })
                    : t('config.3d.failed')}
              </Statement>
            )}
            {/* ST-CONFIG-3D-SLOW — a quiet offer, NEVER an automatic switch. */}
            {slowRender ? (
              <div>
                <Statement>{t('config.3d.slow')}</Statement>
                <Button onClick={() => setHero('spec')}>{t('config.3d.slow.action')}</Button>
              </div>
            ) : null}

            {/* RULING-010 / CLS-0: MirrorHero holds the reserved, room-painted
                60svh box itself and imports no three.js, so it renders at full
                height immediately. The Suspense for the lazy 3D chunk lives
                INSIDE that box (see MirrorHero), not around it — the box can no
                longer collapse to a skeleton while the chunk loads
                (performance.md Finding). */}
            {effectiveHero === 'mirror' && measurements !== null ? (
              <MirrorHero
                measurements={measurements}
                configuration={evaluation.configuration}
                clothId={evaluation.clothContext?.clothId ?? null}
                onUnavailable={(info) => setHeroUnavailable(info)}
                onSlow={() => setSlowRender(true)}
              />
            ) : (
              <WrittenSpecHero />
            )}
          </div>

          <OptionColumn
            options={evaluation.options.filter((o) => !o.optionId.startsWith('commission.'))}
            notApplicable={evaluation.notApplicable}
            onChange={draft.setOption}
            busyOptionId={draft.busyOptionId}
            warnings={evaluation.warnings}
          />
        </div>
      )}

      {evaluation === null ? null : (
        <>
          <PriceDisplay
            state={
              !clothChosen
                ? 'unavailable'
                : draft.busyOptionId !== null
                  ? 'recomputing'
                  : evaluation.quote === null
                    ? 'failed'
                    : 'current'
            }
            quote={evaluation.quote}
            tailorName={tailor?.displayName ?? ''}
            onRetry={draft.reload}
            onOpenBreakdown={() => setBreakdownOpen((open) => !open)}
            breakdownOpen={breakdownOpen}
            onGoToOption={(optionId) =>
              document.getElementById(`option-${optionId}`)?.scrollIntoView()
            }
          />

          {/* ST-CONFIG-INCOMPLETE — the forward action is NEVER disabled. */}
          {pressedForward && missingRequired.length > 0 ? (
            <Statement>
              {t('config.incomplete.forward', {
                optionLabel: tDynamic(`config.option.${missingRequired[0] as string}.label`),
              })}
            </Statement>
          ) : null}
          <div className="pt-actions">
            <Button
              variant="primary"
              onClick={() => {
                setPressedForward(true);
                if (missingRequired.length > 0) {
                  const target = document.getElementById(`option-${missingRequired[0] as string}`);
                  target?.scrollIntoView();
                  const focusable = target?.querySelector('button');
                  if (focusable instanceof HTMLElement) focusable.focus();
                  return;
                }
                navigate('/commission/review');
              }}
            >
              {t('config.complete.forward')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );

  /** `ST-CONFIG-SPEC` — the complete written specification, live and typeset,
   * SUFFICIENT ON ITS OWN TO REACH REVIEW. */
  function WrittenSpecHero() {
    if (evaluation === null) return null;
    const binding = evaluation.measurementBinding ?? null;
    return (
      <section aria-label={t('config.spec.heading')}>
        <h2 className="pt-cutsheet__blockTitle">{t('config.spec.heading')}</h2>
        {binding === null ? null : (
          <FactLine>
            {t('config.spec.boundRecord', {
              authorName: binding.authoredByName,
              takenAtDate: binding.takenAt,
            })}
          </FactLine>
        )}
        {evaluation.quote === null ? null : (
          <FactLine>
            {t('config.spec.metreage', {
              metreage: decimal(evaluation.quote.clothRequirementMetres, 1),
            })}
          </FactLine>
        )}
        <dl>
          {evaluation.options
            .filter((option) => option.currentValue != null)
            .map((option) => (
              <div className="pt-fact pt-fact--pair" key={option.optionId}>
                <dt className="pt-fact__label">
                  {tDynamic(`config.option.${option.optionId}.label`)}
                </dt>
                <dd className="pt-fact__value">
                  {option.type === 'number' || option.type === 'text'
                    ? String(option.currentValue)
                    : tDynamic(
                        `config.option.${option.optionId}.value.${String(option.currentValue)}`,
                      )}
                </dd>
              </div>
            ))}
        </dl>
      </section>
    );
  }
}

/**
 * The mirror, mounted through its documented API and nothing else.
 *
 * FOUR OBLIGATIONS THIS SURFACE OWNS (`src/three/README.md` §3, §5):
 *
 *  1. RULING-010 load order — the room exists BEFORE the garment does. The
 *     viewport reserves its final box and is painted
 *     `color.surface.fabricSurround.room` by this DOM, so the first frame is
 *     never white and there is zero layout shift when the scene arrives.
 *  2. D-006 labelling — the caption is WEB'S DOM, never painted into the canvas:
 *     what the view is for, that colour is judged from the swatch, and the
 *     honest list of what this fidelity does NOT show, including the cloth's
 *     own pattern when `renders.pattern === false`.
 *  3. `ST-CONFIG-3D-HINT` — one first-use hint, in the caption area beneath the
 *     viewport and never over the garment, dismissed by `onFirstInteraction`
 *     and shown ONCE EVER per client.
 *  4. His viewpoint is his — `onCameraChange` is persisted and handed back as
 *     `initialCamera`, so a cloth swap or a navigation does not take it back.
 *
 * The capability check runs in `support.ts`, which imports no three.js, so a
 * browser that cannot render the view never downloads a renderer.
 */
function MirrorHero({
  measurements,
  configuration,
  clothId,
  onUnavailable,
  onSlow,
}: {
  measurements: Record<string, number | string>;
  configuration: Readonly<Record<string, string | number | boolean>>;
  clothId: string | null;
  onUnavailable: (info: { reason: string; missingFields: readonly string[] }) => void;
  onSlow: () => void;
}) {
  const [fabric, setFabric] = useState<GarmentFabric | null>(null);
  const [ready, setReady] = useState<GarmentReadyInfo | null>(null);
  const [hintDismissed, setHintDismissed] = useState(() => hintAlreadySeen());
  const [supported, setSupported] = useState<boolean | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // The capability and requirement checks live in `support.ts`, which imports no
  // three.js — so a browser that cannot render never pays for a renderer
  // (`src/three/README.md` §4). They are loaded HERE rather than at module
  // scope so the written-specification path never pulls them in at all.
  useEffect(() => {
    let live = true;
    void Promise.all([import('../three/support.ts'), import('../three/fabric.ts')]).then(
      ([support, fabricModule]) => {
        if (!live) return;
        const capability = support.detectWebGL();
        if (!capability.supported) {
          setSupported(false);
          onUnavailable({ reason: 'webgl-unsupported', missingFields: [] });
          return;
        }
        const missing = support.missingSilhouetteFields(measurements, configuration);
        if (missing.length > 0) {
          setSupported(false);
          onUnavailable({ reason: 'measurements-incomplete', missingFields: missing });
          return;
        }
        setSupported(true);
        setFabric(clothId === null ? null : fabricModule.garmentFabric(clothId));
      },
    );
    return () => {
      live = false;
    };
  }, [measurements, configuration, clothId, onUnavailable]);

  const unavailable = supported !== true;

  return (
    <div>
      {/* The room, reserved and PAINTED, before the scene exists — and it holds
          this height whether or not the 3D chunk has arrived. The Suspense for
          the lazy renderer is INSIDE the reserved box, with a null fallback, so
          a cold/throttled load shows the room (RULING-010), never a collapsed
          skeleton (CLS-0, performance.md Finding). */}
      <div className="pt-config__viewport">
        <Suspense fallback={null}>
          {unavailable ? null : (
            <GarmentPreview
              measurements={measurements}
              configuration={configuration}
              fabric={fabric}
              ariaLabel={t('config.3d.canvas.aria')}
              reducedMotion={reducedMotion}
              {...(rememberedCamera() === null ? {} : { initialCamera: rememberedCamera() as GarmentCameraState })}
              onUnavailable={(info) =>
                onUnavailable({ reason: info.reason, missingFields: info.missingFields })
              }
              onReady={setReady}
              onFirstInteraction={() => {
                rememberHintSeen();
                setHintDismissed(true);
              }}
              onCameraChange={rememberCamera}
              onPerformanceConcern={onSlow}
            />
          )}
        </Suspense>
      </div>

      {/* ST-CONFIG-3D-CAPTION — persistent, non-dismissible. */}
      <Statement>{t('config.3d.caption')}</Statement>

      {/* What this fidelity does NOT show, stated rather than left to look as
          though it were shown. The pattern clause is the one most easily
          forgotten: on a check or a stripe the shape is right and the pattern
          is absent entirely. */}
      {ready === null ? null : (
        <NotRepresentedLine ready={ready} />
      )}

      {/* ST-CONFIG-3D-HINT — once ever, beneath the viewport, never over the
          garment, and never re-shown. */}
      {hintDismissed ? null : <FactLine>{t('config.3d.hint')}</FactLine>}
    </div>
  );
}

/** The honest list of options and cloth facts the mirror does not represent. */
function NotRepresentedLine({ ready }: { ready: GarmentReadyInfo }) {
  const labels = ready.notRepresented
    .map((optionId) => optionalLabel(`config.option.${optionId}.label`))
    .filter((label): label is string => label !== null);
  if (labels.length === 0) return null;
  return <FactLine>{joinList(labels)}</FactLine>;
}

/* --------------------------------------------- his viewpoint, and the hint */

const CAMERA_KEY = 'pt-garment-camera';
const HINT_KEY = 'pt-garment-hint-seen';

function rememberCamera(state: GarmentCameraState): void {
  try {
    window.sessionStorage.setItem(CAMERA_KEY, JSON.stringify(state));
  } catch {
    /* A viewpoint we cannot persist is not worth an error to the client. */
  }
}

function rememberedCamera(): GarmentCameraState | null {
  try {
    const held = window.sessionStorage.getItem(CAMERA_KEY);
    return held === null ? null : (JSON.parse(held) as GarmentCameraState);
  } catch {
    return null;
  }
}

/** Once EVER per client, not once per session (MOTION_SPEC §4.5). */
function hintAlreadySeen(): boolean {
  try {
    return window.localStorage.getItem(HINT_KEY) === 'seen';
  } catch {
    return true;
  }
}

function rememberHintSeen(): void {
  try {
    window.localStorage.setItem(HINT_KEY, 'seen');
  } catch {
    /* nothing to do */
  }
}

/** MOTION_SPEC §7.2 — a LIVE listener. Reading once at boot is a bug: the
 *  client who changes the setting mid-session is the client who needed it. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (query === undefined) return;
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    setReduced(query.matches);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}
